/* eslint-disable react-hooks/exhaustive-deps */
/* ============================================================
   หน้าแสดงข้อมูลใบรับสินค้า (Goods Receipt — View)

   อ่านอย่างเดียว ไม่แก้ไขข้อมูลใด ๆ ใช้ API ตัวเดียวกับหน้าแก้ไข
   (GET /ERP/goodsreceipt/manage.php?code=)

   แต่ละรายการกางดูล็อต/วันหมดอายุที่รับเข้ามาได้ (er_grlist)
   และกดเลขที่ใบสั่งซื้อเพื่อข้ามไปดูใบสั่งซื้อต้นทางได้เลย

   เข้ามาจากการกดเลขที่เอกสารในหน้ารายการ โดยส่ง config มาทาง
   location.state เหมือนหน้า view อื่น ๆ ของ ERP
============================================================ */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import {
  Button,
  Card,
  Col,
  Descriptions,
  Flex,
  Row,
  Space,
  Table,
  Typography,
  Tag,
  message,
  Modal,
} from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  RollbackOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { TbClipboardCheck, TbBarcode } from "react-icons/tb";
import dayjs from "dayjs";

import GoodsReceiptService from "../../../service/ERP/GoodsReceipt.service";
import OptionService from "../../../service/ERP/Options.service";
import { productColumnView, expireColumnView } from "./model";
import { formatMoney } from "../../../utils/util";
import useGoBack from "../../../hooks/useGoBack";

const grservice = GoodsReceiptService();
const opservice = OptionService();

const gotoFrom = "/ERP/goods-receipt";
const dateFormat = "DD/MM/YYYY";

const showDate = (v) => (v ? dayjs(v).format(dateFormat) : "-");
const showText = (v) =>
  v === null || v === undefined || String(v).trim() === "" ? "-" : v;

function GoodsReceiptView() {
  const navigate = useNavigate();
  const location = useLocation();

  const { config } = location.state || { config: null };

  const [header, setHeader] = useState({});
  const [detail, setDetail] = useState([]);
  const [unitOption, setUnitOption] = useState([]);

  const grcode = header?.grcode ?? config?.code;

  /* กลับไปหน้าที่กดรหัสเอกสารมา ถ้าไม่มีประวัติค่อยกลับหน้ารายการ */
  const handleClose = useGoBack(gotoFrom);

  const handleEdit = () => {
    navigate(`${gotoFrom}/manage/edit`, {
      state: {
        config: {
          title: "แก้ไขใบรับสินค้า",
          textOk: null,
          textCancel: null,
          action: "edit",
          code: grcode,
        },
      },
    });
  };

  const handleQc = () => {
    navigate(`${gotoFrom}/qc`, {
      state: {
        config: {
          title: "บันทึกตรวจรับวัตถุดิบ",
          action: "qc",
          code: grcode,
        },
      },
    });
  };

  const handleTags = () => {
    navigate(`${gotoFrom}/tags`, {
      state: {
        config: { title: "แยก Tags", action: "tags", code: grcode },
      },
    });
  };

  /* กดเลขที่ใบสั่งซื้อในตาราง -> เปิดหน้าแสดงข้อมูลใบสั่งซื้อใบนั้น */
  const handleOpenPo = (record) => {
    if (!record?.pocode) return;
    navigate("/ERP/purchase-order/view", {
      state: {
        config: {
          title: "ใบสั่งซื้อ",
          action: "view",
          code: record.pocode,
        },
      },
    });
  };

  useEffect(() => {
    if (!config?.code) {
      handleClose();
      return;
    }

    const initial = async () => {
      const res = await grservice
        .get(config?.code)
        .catch(() => message.error("โหลดข้อมูลใบรับสินค้าไม่สำเร็จ"));

      if (!res) return;

      const { header: h, detail: d } = res.data || {};
      setHeader(h || {});
      setDetail(d || []);

      const [unitRes] = await Promise.all([
        opservice.optionsUnit({ p: "unit-option" }),
      ]).catch(() => [{ data: { data: [] } }]);

      setUnitOption(unitRes?.data?.data || []);
    };

    initial();
    return () => {};
  }, []);

  /* ยอดสรุปท้ายบิล — ใช้ค่าที่บันทึกไว้ในหัวเอกสาร ไม่คำนวณใหม่ */
  const totalPrice = Number(header?.total_price || 0);
  const vat = Number(header?.vat || 0);
  const vatAmount = (totalPrice * vat) / 100;
  const grandTotal = Number(header?.grand_total_price || 0);

  const masterItems = [
    { label: "รหัสใบรับสินค้า", children: showText(header?.grcode) },
    {
      /* สถานะการรับเข้า — แสดงจุดเดียวตรงหัวเอกสาร */
      label: "สถานะ",
      children:
        header?.doc_status === "ยกเลิก" ? (
          <Tag color="red">ยกเลิกการรับเข้า</Tag>
        ) : (
          <Tag color="green">รับสินค้าแล้ว</Tag>
        ),
    },
    { label: "วันที่ใบรับสินค้า", children: showDate(header?.grdate) },
    { label: "การชำระเงิน", children: showText(header?.payment) },
    { label: "รหัสผู้ขาย", children: showText(header?.supcode) },
    {
      label: "ชื่อผู้ขาย",
      children: showText(header?.supname),
      span: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 },
    },
    {
      label: "ที่อยู่ผู้ขาย",
      children: showText(header?.address),
      span: { xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 },
    },
    { label: "ผู้ติดต่อ", children: showText(header?.contact) },
    { label: "โทรศัพท์", children: showText(header?.tel) },
    { label: "แฟกซ์", children: showText(header?.fax) },
    { label: "เลขที่เอกสารผู้จำหน่าย", children: showText(header?.inv_no) },
    {
      label: "วันที่เอกสารผู้จำหน่าย",
      children: showDate(header?.inv_date),
      span: { xs: 1, sm: 1, md: 2, lg: 2, xl: 2, xxl: 2 },
    },
    {
      label: "หมายเหตุ",
      children: showText(header?.remark),
      span: { xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 },
    },
  ];

  const SummaryRow = ({ label, value, strong = false }) => (
    <Flex justify="end" align="center" gap={16}>
      <Typography.Text style={{ minWidth: 120, textAlign: "right" }}>
        {label}
      </Typography.Text>
      <Typography.Text
        type={strong ? "danger" : undefined}
        strong={strong}
        style={{ minWidth: 130, textAlign: "right" }}
      >
        {value}
      </Typography.Text>
      <Typography.Text style={{ minWidth: 40 }}>Baht</Typography.Text>
    </Flex>
  );

  /* โหลดหัวเอกสารใหม่หลังเปลี่ยนสถานะ จะได้เห็นผลทันทีโดยไม่ต้องออกจากหน้า */
  const reloadHeader = () =>
    grservice
      .get(grcode)
      .then((res) => setHeader(res.data?.header || {}))
      .catch(() => {});

  /* ยกเลิกการรับเข้า : ถอนของออกจากสต๊อกทั้งใบ เพื่อกลับไปแก้ไขใบแล้วรับเข้าใหม่ */
  const handleCancelReceive = () => {
    Modal.confirm({
      title: `ยกเลิกการรับเข้า ${grcode || ""}`,
      content:
        "ของทั้งใบจะถูกถอนออกจากสต๊อก และคืนยอดรับของให้ใบสั่งซื้อ ต้องการดำเนินการต่อหรือไม่?",
      okText: "ยกเลิกการรับเข้า",
      cancelText: "ปิด",
      okButtonProps: { danger: true },
      onOk: () =>
        grservice
          .cancelReceive(grcode)
          .then((res) => {
            message.success(res?.data?.message || "ยกเลิกการรับเข้าเรียบร้อย");
            reloadHeader();
          })
          .catch((err) =>
            message.error(err?.response?.data?.message || "ทำรายการไม่สำเร็จ"),
          ),
    });
  };

  /* ยืนยันรับสินค้าอีกครั้ง : นำล็อตเดิมกลับเข้าสต๊อก */
  const handleConfirmReceive = () => {
    Modal.confirm({
      title: `ยืนยันการรับสินค้า ${grcode || ""}`,
      content:
        "ระบบจะนำล็อตในใบนี้กลับเข้าสต๊อก และบวกยอดรับของให้ใบสั่งซื้ออีกครั้ง",
      okText: "ยืนยันรับสินค้า",
      cancelText: "ปิด",
      onOk: () =>
        grservice
          .confirmReceive(grcode)
          .then((res) => {
            message.success(res?.data?.message || "ยืนยันการรับสินค้าเรียบร้อย");
            reloadHeader();
          })
          .catch((err) =>
            message.error(err?.response?.data?.message || "ทำรายการไม่สำเร็จ"),
          ),
    });
  };

  const isCancelled = header?.doc_status === "ยกเลิก";

  const ButtonsLeft = (
    <Space align="center">
      <Button
        style={{ width: 120 }}
        icon={<ArrowLeftOutlined />}
        onClick={handleClose}
      >
        กลับ
      </Button>
    </Space>
  );

  const ButtonsRight = (
    <Space
      align="center"
      className="flex justify-start lg:justify-end md:justify-end"
    >
      {isCancelled ? (
        <Button
          icon={<CheckCircleOutlined />}
          className="bn-center justify-center bn-success-outline"
          onClick={handleConfirmReceive}
        >
          ยืนยันรับสินค้าอีกครั้ง
        </Button>
      ) : (
        <Button
          danger
          icon={<RollbackOutlined />}
          className="bn-center justify-center"
          onClick={handleCancelReceive}
        >
          ยกเลิกการรับเข้า
        </Button>
      )}
      <Button
        icon={<TbClipboardCheck style={{ fontSize: "1rem" }} />}
        className="bn-center justify-center bn-success-outline"
        onClick={handleQc}
      >
        บันทึกตรวจรับ (QC)
      </Button>
      <Button
        icon={<TbBarcode style={{ fontSize: "1rem" }} />}
        className="bn-center justify-center bn-info-outline"
        onClick={handleTags}
      >
        แยก Tags / Barcode
      </Button>
      <Button
        style={{
          borderColor: "#52c41a",
          color: "#fff",
          background: "#52c41a",
        }}
        icon={<EditOutlined />}
        onClick={handleEdit}
      >
        แก้ไขใบรับสินค้า
      </Button>
    </Space>
  );

  return (
    <div className="goodsreceipt-view">
      <Space
        direction="vertical"
        size="middle"
        style={{ display: "flex", position: "relative" }}
      >
        <Row
          gutter={[{ xs: 32, sm: 32, md: 32, lg: 12, xl: 12 }, 8]}
          className="m-0"
        >
          <Col xs={24} sm={24} md={10} lg={8} xl={8} className="px-0">
            {ButtonsLeft}
          </Col>
          <Col xs={24} sm={24} md={14} lg={16} xl={16} className="px-0">
            {ButtonsRight}
          </Col>
        </Row>

        <Descriptions
          bordered
          size="small"
          column={{ xs: 1, sm: 2, md: 3, lg: 3, xl: 3, xxl: 3 }}
          items={masterItems}
        />

        <Card
          title={
            <Typography.Title className="m-0 !text-zinc-800" level={4}>
              รายการสินค้าที่รับเข้า
            </Typography.Title>
          }
        >
          <Table
            bordered
            size="small"
            rowKey={(r, i) => `${r?.stcode ?? "row"}-${r?.pocode ?? ""}-${i}`}
            dataSource={detail}
            columns={productColumnView(unitOption, { openPo: handleOpenPo })}
            pagination={false}
            scroll={{ x: "max-content" }}
            locale={{ emptyText: <span>ไม่มีรายการสินค้า</span> }}
            /* กางดูล็อตที่รับเข้ามาของสินค้าแต่ละรายการ
               แถวไหนไม่มีล็อตก็ไม่ต้องมีปุ่มกาง */
            expandable={{
              rowExpandable: (record) => (record?.expire || []).length > 0,
              expandedRowRender: (record) => (
                <Table
                  bordered
                  size="small"
                  rowKey={(r) => r?.lot_no ?? r?.seq}
                  dataSource={record?.expire || []}
                  columns={expireColumnView}
                  pagination={false}
                  style={{ backgroundColor: "#fafafa" }}
                />
              ),
            }}
          />

          {detail.length > 0 && (
            <Flex vertical gap={4} className="mt-3">
              <SummaryRow label="ยอดรวม" value={formatMoney(totalPrice, 2)} />
              <SummaryRow
                label={`ภาษีมูลค่าเพิ่ม ${vat}%`}
                value={formatMoney(vatAmount, 2)}
              />
              <SummaryRow
                label="ยอดสุทธิ"
                value={formatMoney(grandTotal, 2)}
                strong
              />
            </Flex>
          )}
        </Card>

        <Row
          gutter={[{ xs: 32, sm: 32, md: 32, lg: 12, xl: 12 }, 8]}
          className="mx-0 mb-4"
        >
          <Col xs={24} sm={24} md={12} lg={12} xl={12} className="px-0">
            {ButtonsLeft}
          </Col>
        </Row>
      </Space>
    </div>
  );
}

export default GoodsReceiptView;
